import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Receipt, Ticket, CopySimple, Package } from "@phosphor-icons/react";
import { Shell } from "@/components/Shell";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";

const money = (v, cur = "EUR") => `€ ${Number(v || 0).toFixed(2)}`;

const STATUS = {
  paid: { key: "orderStatusPaid", cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  pending: { key: "orderStatusPending", cls: "bg-amber-100 text-amber-700 border-amber-300" },
  failed: { key: "orderStatusFailed", cls: "bg-red-100 text-red-700 border-red-300" },
};

export default function Orders() {
  const { t } = useApp();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/shop/orders/mine")
      .then((r) => setOrders(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const copyCode = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.success(t("orderCodeCopied")); }
    catch { toast.error("Fehler"); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <Shell back="/dashboard">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="orders-page">
        <div className="flex items-center gap-2">
          <Receipt size={28} weight="duotone" style={{ color: "var(--app-strong)" }} />
          <div>
            <h1 className="text-2xl font-heading font-semibold text-gray-900">{t("ordersTitle")}</h1>
            <p className="font-body text-sm text-gray-500">{t("ordersSubtitle")}</p>
          </div>
        </div>

        {loading && <p className="font-body text-gray-500">…</p>}
        {!loading && orders.length === 0 && (
          <div className="sb-card p-6 text-center" data-testid="orders-empty">
            <Package size={36} weight="duotone" className="mx-auto text-gray-300" />
            <p className="font-body text-gray-600 mt-2">{t("ordersEmpty")}</p>
          </div>
        )}

        <div className="space-y-4">
          {orders.map((o) => {
            const st = STATUS[o.status] || STATUS.pending;
            const codes = o.activation_codes || [];
            return (
              <div key={o.id} data-testid={`order-${o.id}`} className="sb-card p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-heading font-semibold text-gray-900">{o.product_name || "SafeBuddy"}</p>
                    <p className="font-body text-xs text-gray-500 mt-0.5">{fmtDate(o.created_at)}</p>
                  </div>
                  <span className={`shrink-0 text-xs font-heading font-medium px-2.5 py-1 rounded-full border ${st.cls}`} data-testid={`order-status-${o.id}`}>{t(st.key)}</span>
                </div>

                <div className="mt-3 space-y-1">
                  {(o.items || []).map((li, idx) => (
                    <div key={idx} className="flex items-center justify-between font-body text-sm text-gray-700">
                      <span>{li.product_name}{li.color?.name ? ` · ${li.color.name}` : ""}{li.motif?.name ? ` · ${li.motif.name}` : ""} × {li.quantity}</span>
                      <span>{money(li.line_total ?? (li.unit_price * li.quantity))}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-3 pt-3 border-t border-gray-200 space-y-1 font-body text-sm">
                  {o.discount_amount > 0 && (
                    <div className="flex items-center justify-between text-emerald-700">
                      <span>{t("orderDiscount")}{o.discount_code ? ` (${o.discount_code})` : ""}</span>
                      <span>− {money(o.discount_amount)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-gray-600">
                    <span>{t("orderShipping")}</span>
                    <span>{o.shipping_cost > 0 ? money(o.shipping_cost) : t("orderFree")}</span>
                  </div>
                  <div className="flex items-center justify-between font-heading font-semibold text-gray-900 text-base">
                    <span>{t("orderTotal")}</span>
                    <span data-testid={`order-total-${o.id}`}>{money(o.amount)}</span>
                  </div>
                </div>

                {codes.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Ticket size={16} weight="bold" style={{ color: "var(--app-strong)" }} />
                      <p className="font-heading font-medium text-sm text-gray-800">{t("orderCodes")}</p>
                    </div>
                    <div className="space-y-2" data-testid={`order-codes-${o.id}`}>
                      {codes.map((c) => (
                        <div key={c} className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
                          <span className="font-mono font-semibold text-gray-900 tracking-wider text-sm">{c}</span>
                          <button data-testid={`copy-code-${c}`} onClick={() => copyCode(c)} className="sb-btn px-2.5 py-1.5 bg-white text-xs flex items-center gap-1 shrink-0"><CopySimple size={14} weight="bold" />{t("orderCopy")}</button>
                        </div>
                      ))}
                    </div>
                    <p className="font-body text-xs text-gray-500 mt-2">{t("orderCodesHint")}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </motion.div>
    </Shell>
  );
}
